import React, { useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Button } from '../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { SkeletonTable } from '../../components/ui/Skeleton';
import { usePricingFetch, fetchNextPage } from '../../hooks/usePricingFetch';
import { regions, formatPrice } from '../../config/pricingConfig';

const AKS_TIERS = [
  { value: 'Standard_D2s_v3', label: 'Standard_D2s_v3 (2 vCPU, 8 GiB)' },
  { value: 'Standard_D4s_v3', label: 'Standard_D4s_v3 (4 vCPU, 16 GiB)' },
  { value: 'Standard_D8s_v3', label: 'Standard_D8s_v3 (8 vCPU, 32 GiB)' },
  { value: 'Standard_D16s_v3', label: 'Standard_D16s_v3 (16 vCPU, 64 GiB)' },
  { value: 'Standard_DS2_v2', label: 'Standard_DS2_v2 (2 vCPU, 7 GiB)' },
  { value: 'Standard_DS3_v2', label: 'Standard_DS3_v2 (4 vCPU, 14 GiB)' },
  { value: 'Standard_F4s_v2', label: 'Standard_F4s_v2 (4 vCPU, 8 GiB)' },
  { value: 'Standard_F8s_v2', label: 'Standard_F8s_v2 (8 vCPU, 16 GiB)' },
];

const AKSPricing = () => {
  const [region, setRegion] = useState('');
  const [nodeSku, setNodeSku] = useState('');
  const [searchParams, setSearchParams] = useState(null);
  const [extraItems, setExtraItems] = useState([]);
  const [nextPageUrl, setNextPageUrl] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [sortKey, setSortKey] = useState(null);
  const [sortAsc, setSortAsc] = useState(true);

  const filter = searchParams
    ? `armRegionName eq '${searchParams.region}' and armSkuName eq '${searchParams.nodeSku}' and priceType eq 'Consumption' and serviceFamily eq 'Compute' and contains(productName, 'Cloud Services') ne true and contains(skuName, 'Spot') ne true and contains(skuName, 'Low Priority') ne true`
    : '';

  const { items, isLoading, error, nextPageLink } = usePricingFetch({
    queryKey: `aks-${filter}`,
    filter,
    enabled: !!searchParams,
  });

  const handleFetch = () => {
    setSearchParams({ region, nodeSku });
    setExtraItems([]);
    setNextPageUrl(null);
  };

  const handleLoadMore = async () => {
    const url = nextPageUrl || nextPageLink;
    if (!url) return;
    setLoadingMore(true);
    try {
      const data = await fetchNextPage(url);
      setExtraItems((prev) => [...prev, ...(data.Items || [])]);
      setNextPageUrl(data.NextPageLink || null);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleSort = (key) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(true); }
  };

  const SortIndicator = ({ col }) =>
    sortKey === col ? (sortAsc ? ' ↑' : ' ↓') : null;

  const allItems = [...items, ...extraItems];
  const sortedItems = sortKey
    ? [...allItems].sort((a, b) => {
        const va = a[sortKey]; const vb = b[sortKey];
        return sortAsc
          ? (typeof va === 'number' ? va - vb : String(va).localeCompare(String(vb)))
          : (typeof vb === 'number' ? vb - va : String(vb).localeCompare(String(va)));
      })
    : allItems;

  return (
    <div>
      <h2 className="text-2xl font-bold mb-2">AKS Node Pool Pricing</h2>
      <p className="text-sm text-gray-500 mb-4">
        AKS nodes are billed at the underlying VM rate. Select a node SKU to see compute pricing.
      </p>
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <Select value={region} onValueChange={setRegion}>
          <SelectTrigger className="w-full sm:w-[200px]"><SelectValue placeholder="Select Region" /></SelectTrigger>
          <SelectContent>{regions.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={nodeSku} onValueChange={setNodeSku}>
          <SelectTrigger className="w-full sm:w-[260px]"><SelectValue placeholder="Select Node SKU" /></SelectTrigger>
          <SelectContent>{AKS_TIERS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
        </Select>
        <Button onClick={handleFetch} disabled={isLoading || !region || !nodeSku}>
          {isLoading ? 'Loading…' : 'Fetch Prices'}
        </Button>
      </div>
      {error && <div className="text-red-500 mb-4 text-sm">{error.message}</div>}
      {isLoading && <SkeletonTable rows={4} cols={3} />}
      {!isLoading && sortedItems.length > 0 && (
        <>
          <p className="text-sm text-gray-500 mb-2">Showing {sortedItems.length} result(s)</p>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="cursor-pointer select-none" onClick={() => handleSort('skuName')}>SKU<SortIndicator col="skuName" /></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => handleSort('productName')}>Product<SortIndicator col="productName" /></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => handleSort('unitPrice')}>Unit Price<SortIndicator col="unitPrice" /></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedItems.map((price) => (
                <TableRow key={`${price.skuId}-${price.meterName}`}>
                  <TableCell>{price.skuName}</TableCell>
                  <TableCell>{price.productName}</TableCell>
                  <TableCell>{formatPrice(price.unitPrice, price.currencyCode, price.unitOfMeasure)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {(nextPageUrl || nextPageLink) && (
            <div className="mt-4 text-center">
              <Button onClick={handleLoadMore} disabled={loadingMore} className="bg-gray-100 text-gray-700 hover:bg-gray-200">
                {loadingMore ? 'Loading…' : 'Load More'}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AKSPricing;
